/**
 * VariableInput Component
 *
 * Input field with autocomplete for workflow context variables.
 * Triggers autocomplete when user types {{ and shows available variables.
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Input } from '@/shared/components/ui/Input';
import { useVariableSuggestions } from '../hooks/useVariableSuggestions';
import type { AvailableContext } from '../utils/node-schemas';
import type { ContextVariable } from '../types/variables';
import { CATEGORY_INFO } from '../utils/context-variables';

interface VariableInputProps {
  /** Current value */
  value: string;
  /** Change handler */
  onChange: (value: string) => void;
  /** Available context for variable suggestions */
  availableContext: AvailableContext;
  /** Input placeholder */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Input type (defaults to text) */
  type?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Identificador para os testes de navegador (repassado ao input real) */
  'data-testid'?: string;
}

/**
 * Suggestion item component
 */
const SuggestionItem: React.FC<{
  variable: ContextVariable;
  isSelected: boolean;
  searchTerm: string;
  onClick: () => void;
  onMouseEnter: () => void;
}> = ({ variable, isSelected, searchTerm, onClick, onMouseEnter }) => {
  // Highlight matching text
  const highlightMatch = (text: string) => {
    if (!searchTerm) return text;

    const lowerText = text.toLowerCase();
    const lowerSearch = searchTerm.toLowerCase();
    const index = lowerText.indexOf(lowerSearch);

    if (index === -1) return text;

    return (
      <>
        {text.slice(0, index)}
        <span className="bg-yellow-200 text-yellow-900">{text.slice(index, index + searchTerm.length)}</span>
        {text.slice(index + searchTerm.length)}
      </>
    );
  };

  return (
    <button
      type="button"
      className={cn(
        'w-full px-3 py-2 text-left flex items-center justify-between gap-2 hover:bg-gray-100 transition-colors',
        isSelected && 'bg-blue-50'
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
    >
      <div className="flex-1 min-w-0">
        <div className="text-xs font-mono text-gray-600 truncate">
          {highlightMatch(variable.fullPath)}
        </div>
        <div className="text-xs text-gray-500 truncate">
          {highlightMatch(variable.label)}
        </div>
      </div>
      {variable.type && (
        <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-500 flex-shrink-0">
          {variable.type}
        </span>
      )}
    </button>
  );
};

/**
 * Suggestions dropdown component
 */
const SuggestionsDropdown: React.FC<{
  groupedSuggestions: Record<string, ContextVariable[]>;
  suggestions: ContextVariable[];
  selectedIndex: number;
  searchTerm: string;
  position: { top: number; left: number; width: number };
  onSelect: (variable: ContextVariable) => void;
  onHover: (index: number) => void;
}> = ({ groupedSuggestions, suggestions, selectedIndex, searchTerm, position, onSelect, onHover }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Scroll selected item into view
  useEffect(() => {
    if (dropdownRef.current) {
      const selected = dropdownRef.current.querySelector('[data-selected="true"]');
      if (selected) {
        selected.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  // Calculate global index for each item
  let globalIndex = 0;

  return createPortal(
    <div
      ref={dropdownRef}
      className="fixed z-[9999] bg-white rounded-lg shadow-lg border border-gray-200 max-h-64 overflow-y-auto"
      style={{
        top: position.top,
        left: position.left,
        width: Math.max(position.width, 320),
      }}
    >
      {Object.entries(groupedSuggestions).map(([category, variables]) => {
        const categoryInfo = CATEGORY_INFO[category];
        return (
          <div key={category}>
            {/* Category header */}
            <div className="sticky top-0 px-3 py-1.5 bg-gray-50 border-b text-xs font-medium text-gray-600 flex items-center gap-1.5">
              <span>{categoryInfo?.icon}</span>
              <span>{categoryInfo?.label || category.toUpperCase()}</span>
            </div>
            {/* Category items */}
            {variables.map((variable) => {
              const currentIndex = globalIndex++;
              const isSelected = currentIndex === selectedIndex;
              return (
                <div key={variable.value} data-selected={isSelected}>
                  <SuggestionItem
                    variable={variable}
                    isSelected={isSelected}
                    searchTerm={searchTerm}
                    onClick={() => onSelect(variable)}
                    onMouseEnter={() => onHover(currentIndex)}
                  />
                </div>
              );
            })}
          </div>
        );
      })}
      {suggestions.length === 0 && (
        <div className="px-3 py-4 text-center text-sm text-gray-500">
          Nenhuma variável encontrada
        </div>
      )}
      {/* Help footer */}
      <div className="sticky bottom-0 px-3 py-1.5 bg-gray-50 border-t text-[10px] text-gray-400 flex justify-between">
        <span>↑↓ navegar</span>
        <span>Enter selecionar</span>
        <span>Esc fechar</span>
      </div>
    </div>,
    document.body
  );
};

/**
 * VariableInput - Input with context variable autocomplete
 */
export const VariableInput: React.FC<VariableInputProps> = ({
  value,
  onChange,
  availableContext,
  placeholder,
  className,
  type = 'text',
  disabled,
  'data-testid': dataTestId,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [cursorPosition, setCursorPosition] = useState(0);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Use suggestions hook
  const {
    showSuggestions,
    suggestions,
    groupedSuggestions,
    searchTerm,
    selectedIndex,
    selectPrevious,
    selectNext,
    setSelectedIndex,
    insertVariable,
    closeSuggestions,
  } = useVariableSuggestions({
    availableContext,
    value,
    cursorPosition,
  });

  // Update dropdown position when suggestions appear
  useEffect(() => {
    if (showSuggestions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showSuggestions]);

  // Handle input change
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
      setCursorPosition(e.target.selectionStart || 0);
    },
    [onChange]
  );

  // Handle cursor position change
  const handleSelect = useCallback((e: React.SyntheticEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    setCursorPosition(target.selectionStart || 0);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!showSuggestions) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          selectNext();
          break;
        case 'ArrowUp':
          e.preventDefault();
          selectPrevious();
          break;
        case 'Enter':
          e.preventDefault();
          if (suggestions[selectedIndex]) {
            const newValue = insertVariable(suggestions[selectedIndex]);
            onChange(newValue);
            // Move cursor to end of inserted variable
            setTimeout(() => {
              if (inputRef.current) {
                const newPos = newValue.indexOf('}}', cursorPosition) + 2;
                inputRef.current.setSelectionRange(newPos, newPos);
                setCursorPosition(newPos);
              }
            }, 0);
          }
          break;
        case 'Escape':
          e.preventDefault();
          closeSuggestions();
          break;
        case 'Tab':
          if (suggestions[selectedIndex]) {
            e.preventDefault();
            const newValue = insertVariable(suggestions[selectedIndex]);
            onChange(newValue);
          }
          break;
      }
    },
    [
      showSuggestions,
      suggestions,
      selectedIndex,
      selectNext,
      selectPrevious,
      insertVariable,
      onChange,
      closeSuggestions,
      cursorPosition,
    ]
  );

  // Handle suggestion selection
  const handleSelectSuggestion = useCallback(
    (variable: ContextVariable) => {
      const newValue = insertVariable(variable);
      onChange(newValue);
      inputRef.current?.focus();
    },
    [insertVariable, onChange]
  );

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type={type}
        value={value}
        onChange={handleChange}
        onSelect={handleSelect}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(
          // Visual hint that variable autocomplete is available
          value.includes('{{') && 'font-mono text-sm',
          className
        )}
        autoComplete="off"
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      />
      {showSuggestions && (
        <SuggestionsDropdown
          groupedSuggestions={groupedSuggestions}
          suggestions={suggestions}
          selectedIndex={selectedIndex}
          searchTerm={searchTerm}
          position={dropdownPosition}
          onSelect={handleSelectSuggestion}
          onHover={setSelectedIndex}
        />
      )}
    </div>
  );
};

VariableInput.displayName = 'VariableInput';

export default VariableInput;
